import React, { useState, useEffect, useMemo } from 'react'
import { Alert, Spinner } from 'react-bootstrap'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import useStores from '../../stores/useStore'
import { useObserver } from 'mobx-react'
import { debounce } from 'throttle-debounce'
import StorehouseItem from './StorehouseItem'
import SearchResultAmount from '../Forms/SearchResultAmount'
import TopLevelTreeItem from '../Offices/TopLevelTreeItem'
import { emptyGuid } from '../../utils/emptyGuid'
import { observer } from 'mobx-react'
import SearchField from '../Forms/SearchField'

const StyledSearchWrap = styled.div`
  margin-bottom: 2rem;
`

const StyledShowMore = styled.span`
  color: ${(props) => props.theme.primaryColor};
  float: right;
  margin-top: 15px;
  cursor: pointer;
`

const StorehousesTree = ({
  onClick,
  isClickable,
  withSearch,
  renderExtraItemContent = () => null,
  parentsWithSelectedChildren,
  getContextMenuOptions,
  showTopLevel,
}) => {
  const [filter, setFilter] = useState('')
  const [filterIsLoading, setFilterIsLoading] = useState(false)
  const [filteredStorehouses, setFilteredStorehouses] = useState([])
  const [storehousesActive, setStorehousesActive] = useState({})
  const [selectedStorehouseId, setSelectedStorehouseId] = useState('')
  const [limit, setLimit] = useState(true)
  const limitLength = 40
  const { t } = useTranslation()
  const { storehouseTreeStore } = useStores()

  // Observables from store
  const storehouseNodes = useObserver(
    () => storehouseTreeStore.loadedStorehouses
  )
  const error = useObserver(() => storehouseTreeStore.error)

  // Effect to load storehouses
  useEffect(() => {
    storehouseTreeStore.getStorehouses(emptyGuid)
  }, [storehouseTreeStore])

  // Effect to expand child storehouses when toggled
  useEffect(() => {
    if (Object.keys(storehousesActive).length) {
      for (const storehouseId of Object.keys(storehousesActive)) {
        if (storehousesActive[storehouseId]) {
          storehouseTreeStore.getStorehouses(storehouseId)
        }
      }
    }
  }, [storehouseTreeStore, storehousesActive])

  useEffect(() => {
    return () => {
      storehouseTreeStore.clearError() // Clear the error when the component unmounts
    }
  }, [storehouseTreeStore])

  // Debounced filter change for searching storehouses
  const loadFilteredStorehousesDebounced = useMemo(
    () =>
      debounce(500, async (value) => {
        if (!value) {
          storehouseTreeStore.cancelGetFilteredStorehouseTrees()
          setLimit(false)
          setFilteredStorehouses([])
          return
        }

        try {
          setLimit(true)
          setFilteredStorehouses([])
          setFilterIsLoading(true)

          let results = await storehouseTreeStore.getFilteredStorehouses(value)

          if (!results.length) {
            const all = storehouseTreeStore.loadedStorehouses
            results = all.filter((s) =>
              s.displayName.toLowerCase().includes(value.toLowerCase())
            )
          }

          setFilteredStorehouses(results)
        } catch (error) {
          setFilteredStorehouses([])
        } finally {
          setFilterIsLoading(false)
        }
      }),
    [storehouseTreeStore]
  )

  const handleChangeFilter = (e) => {
    const { value } = e.target
    setFilter(value)
    if (value === '') {
      // user cleared the input
      setFilteredStorehouses([])
      setLimit(false)
      storehouseTreeStore.cancelGetFilteredStorehouseTrees()
      return
    }
    loadFilteredStorehousesDebounced(value)
  }
  const handleClearClick = () => {
    setFilter('')
    setFilteredStorehouses([])
    setLimit(false)
  }

  // Computed: Top-level storehouses for rendering
  const topLevelStorehouses = useMemo(() => {
    if (filter) {
      return filteredStorehouses
    }
    return storehouseNodes.filter((storehouse) => !storehouse.parent)
  }, [storehouseNodes, filter, filteredStorehouses])

  const limitedTopLevelStorehouses = useMemo(() => {
    if (limit) {
      return topLevelStorehouses.slice(0, limitLength)
    }
    return topLevelStorehouses
  }, [limit, topLevelStorehouses])

  return useObserver(() => (
    <>
      {withSearch && (
        <StyledSearchWrap>
          <SearchField
            type='search'
            placeholder={t('storehouseTree:searchStorehouse')}
            onChange={handleChangeFilter}
            value={filter}
            onClearClick={handleClearClick}
          />
          {filter && <SearchResultAmount count={topLevelStorehouses.length} />}
        </StyledSearchWrap>
      )}
      {error && <Alert variant='danger'>{error}</Alert>}
      {showTopLevel ? (
        <TopLevelTreeItem
          handleClick={onClick}
          renderExtraItemContent={renderExtraItemContent}
        >
          {limitedTopLevelStorehouses.map((storehouse) => (
            <StorehouseItem
              key={storehouse.id}
              storehouse={storehouse}
              setStorehousesActive={setStorehousesActive}
              onClick={onClick}
              storehouseNodes={storehouseNodes}
              isClickable={isClickable}
              storehousesActive={storehousesActive}
              renderExtraItemContent={renderExtraItemContent}
              parentsWithSelectedChildren={parentsWithSelectedChildren}
              selectedStorehouseId={selectedStorehouseId}
              setSelectedStorehouseId={setSelectedStorehouseId}
              getContextMenuOptions={getContextMenuOptions}
              maxUnitType={storehouseTreeStore.maxUnitType}
            />
          ))}
        </TopLevelTreeItem>
      ) : (
        limitedTopLevelStorehouses.map((storehouse) => (
          <StorehouseItem
            key={storehouse.id}
            storehouse={storehouse}
            setStorehousesActive={setStorehousesActive}
            onClick={onClick}
            storehouseNodes={storehouseNodes}
            isClickable={isClickable}
            storehousesActive={storehousesActive}
            renderExtraItemContent={renderExtraItemContent}
            parentsWithSelectedChildren={parentsWithSelectedChildren}
            selectedStorehouseId={selectedStorehouseId}
            setSelectedStorehouseId={setSelectedStorehouseId}
            getContextMenuOptions={getContextMenuOptions}
            maxUnitType={storehouseTreeStore.maxUnitType}
          />
        ))
      )}
      {topLevelStorehouses.length > limitLength && limit && (
        <div className='d-flex justify-content-center'>
          <StyledShowMore onClick={() => setLimit(false)}>
            {t('officeAdmin:showAll')}
          </StyledShowMore>
        </div>
      )}
      {topLevelStorehouses.length === 0 &&
        !storehouseTreeStore.storehousesLoading &&
        !filterIsLoading && (
          <div className='ps-4 pe-2'>
            <strong>{t('officeAdmin:searchStructureTreeNoresult')}</strong>
          </div>
        )}
      {(storehouseTreeStore.storehousesLoading || filterIsLoading) && (
        <div className='d-flex justify-content-center'>
          <Spinner animation='border' variant='primary' />
        </div>
      )}
    </>
  ))
}

export default observer(StorehousesTree)
